import { CircularProgress, Divider, Grid, Stack, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import DialogLayout from "../../../components/layout/DialogLayout";
import { apiGetPayoutInvestorList, apiSavePayoutExit } from "../../../utils/api/api";
import { SNACKBAR_ERROR, SNACKBAR_SUCCESS, TAB_FULL_EXIT } from "../../../utils/constants/constants";
import { isSubAdmin, isValidDate } from "../../../utils/StringUtil";
import { roundUp } from "../../../utils/Validator";

const AddExit = ({ open, toggleDialog, scheme, handleSave, exitType }) => {

    const userType = process.env.REACT_APP_USER_TYPE;
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);

    const [exitDate, setExitDate] = useState(null);
    const [remark, setRemark] = useState("");
    const [invList, setInvList] = useState([]);

    const getInvList = (param = {}) => {
        setIsLoading(true);
        param.oppRid = scheme.oppRid;
        param.exitType = exitType;
        apiGetPayoutInvestorList(param)
            .then((data) => {
                setIsLoading(false);
                setInvList(data);
            })
            .catch((error) => {
                setIsLoading(false);
                enqueueSnackbar("Failed to fetch investor list", SNACKBAR_ERROR);
            });
    };

    const deleteInvestor = (ioRid) => {
        const updatedList = invList.filter((investor) => investor.ioRid !== ioRid);
        setInvList(updatedList);
    };

    const handleInvListChange = (index, field, value) => {
        setInvList((prevInvList) => {
            const updatedList = [...prevInvList];
            updatedList[index][field] = value; // Update the specific field in the specific row
            updatedList[index].error = null;
            return updatedList;
        });
    };

    const calculateNetAmount = (grossAmount, fee, carry, tds) => {
        let netAmount = roundUp((parseFloat(grossAmount) - (parseFloat(carry) + parseFloat(fee) + parseFloat(tds))), 2);
        if (isNaN(netAmount) || netAmount < 0) {
            enqueueSnackbar("Net amount calculation resulted in invalid value", SNACKBAR_ERROR);
            return "";
        }
        return netAmount;
    }

    const saveExit = () => {

        if (!isValidDate(exitDate)) {
            enqueueSnackbar("Invalid date", SNACKBAR_ERROR);
            return
        }

        if (exitType === null || exitType === "") {
            enqueueSnackbar("Invalid exit type", SNACKBAR_ERROR);
            return;
        }

        if (remark.length > 1500) {
            enqueueSnackbar("Maximum characters for remark is 1500", SNACKBAR_ERROR);
            return;
        }

        const requestBody = {
            exitDate: exitDate,
            exitType: exitType,
            remark: remark.replace(/\n/g, "<br/>"),
            oppRid: scheme.oppRid,
            investorExitDetails: invList
        }

        setIsLoading(true);
        apiSavePayoutExit(requestBody)
            .then((response) => {
                setIsLoading(false);
                if (response.status && response.data && Object.keys(response.data).length > 0) {
                    const errors = Object.entries(response.data).map(([key, value]) => ({
                        ioRid: Number(key),
                        error: value,
                    }));

                    // Update FULL invList: attach error only to matching records
                    const updatedInvList = invList.map((inv) => {
                        const matchedError = errors.find((err) => err.ioRid === inv.ioRid);
                        return {
                            ...inv,
                            error: matchedError ? matchedError.error : null, // add error if present
                        };
                    });

                    setInvList(updatedInvList);
                } else {
                    enqueueSnackbar("Exit saved successfully", SNACKBAR_SUCCESS);
                    handleSave();
                }
            })
            .catch((error) => {
                setIsLoading(false);
                enqueueSnackbar(error, SNACKBAR_ERROR);
            });
    }

    useEffect(() => {
        if (open) {
            setExitDate(null);
            setRemark("");
            getInvList();
        }
    }, [open]);

    return (
        <DialogLayout
            open={open}
            title={exitType === TAB_FULL_EXIT ? "Add Full Exit" : "Add Partial Exit"}
            closeAction={toggleDialog}
            negativeAction={(userType === 'ADMIN' && !isSubAdmin()) ? { label: "Cancel", onClick: toggleDialog } : ''}
            positiveAction={
                (userType === 'ADMIN' && !isSubAdmin())
                    ? {
                        label: exitType === TAB_FULL_EXIT ? "Save Full Exit" : "Save Partial Exit",
                        onClick: saveExit
                    }
                    : ''
            }

            maxWidth={"xl"}
        >
            <h3 className="fs-large-semibold text-primary mb-4">{scheme.oppTitle}</h3>
            {
                isLoading ? <CircularProgress /> :

                    <div>
                        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
                            <Grid item xs={5} style={{ marginRight: "500px" }}>
                                <TextField
                                    id="outlined-basic"
                                    type="date"
                                    name="exitDate"
                                    label="Exit Date"
                                    variant="outlined"
                                    value={exitDate}
                                    onChange={(e) => setExitDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    id="outlined-basic"
                                    type="text"
                                    name="remark"
                                    label="Remark"
                                    variant="outlined"
                                    value={remark}
                                    onChange={(e) => setRemark(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    multiline
                                    rows={4}
                                />
                            </Grid>

                            <Grid item xs={12} style={{ paddingBottom: 15, paddingTop: 30 }}>
                                <Divider />
                            </Grid>
                        </Grid>

                        <Stack direction="column ">
                            <table className="table table-borderless w-100">
                                <thead className="sticky-table-header bg-white">
                                    <tr className="fs-med-semibold text-center px-2">
                                        <th scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Investor Name
                                        </th>
                                        <th className="text-start " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Gross Amount
                                        </th>
                                        <th className="text-start " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Fee
                                        </th>
                                        <th className="text-center " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Fee Type
                                        </th>
                                        <th className="text-start " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Carry
                                        </th>
                                        <th className="text-start " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            TDS
                                        </th>
                                        <th className="text-start " scope="col" style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
                                            Net Amount
                                        </th>
                                        {/* <th></th> */}
                                    </tr>
                                </thead>
                                <tbody >
                                    {invList?.length ? (
                                        invList.map((data, index) => (
                                            <React.Fragment key={index}>
                                                <tr className={`fs-normal-medium py-2 text-center ${data?.error ? 'border border-danger' : ''}`}>
                                                    <td className="pt-3">
                                                        <span style={{ whiteSpace: "nowrap" }}>
                                                            {data?.invName ?? ''}
                                                            {data?.invCode && ` (${data.invCode})`}
                                                        </span>
                                                        <div className="fs-small-regular text-muted" style={{ display: "block" }}>{data?.className}</div>

                                                    </td>
                                                    <td className="pt-2 ">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="grossAmount"
                                                                variant="outlined"
                                                                value={data?.grossAmount ?? ''}
                                                                onChange={(e) => {
                                                                    const numericValue = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                    handleInvListChange(index, 'grossAmount', numericValue);
                                                                    handleInvListChange(index, 'netAmount', calculateNetAmount((parseFloat(numericValue) || 0), (parseFloat(data?.fee) || 0), (parseFloat(data?.carry) || 0), (parseFloat(data?.tds) || 0)));

                                                                }}
                                                                size="small"
                                                                sx={{ width: '200px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="pt-2">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="fee"
                                                                variant="outlined"
                                                                value={data?.fee ?? ''}
                                                                onChange={(e) => {
                                                                    const numericValue = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                    handleInvListChange(index, 'fee', numericValue)
                                                                    handleInvListChange(index, 'netAmount', calculateNetAmount((parseFloat(data?.grossAmount) || 0), (parseFloat(numericValue) || 0), (parseFloat(data?.carry) || 0), (parseFloat(data?.tds) || 0)));

                                                                }}
                                                                size="small"
                                                                sx={{ width: '150px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="pt-2">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="feeType"
                                                                variant="outlined"
                                                                value={data?.feeType ?? ''}
                                                                onChange={(e) => handleInvListChange(index, 'feeType', e.target.value)}
                                                                size="small"
                                                                sx={{ width: '400px' }}
                                                                multiline
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="pt-2">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="carry"
                                                                variant="outlined"
                                                                value={data?.carry ?? ''}
                                                                onChange={(e) => {
                                                                    const numericValue = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                    handleInvListChange(index, 'carry', numericValue)
                                                                    handleInvListChange(index, 'netAmount', calculateNetAmount((parseFloat(data?.grossAmount) || 0), (parseFloat(data?.fee) || 0), (parseFloat(numericValue) || 0), (parseFloat(data?.tds) || 0)));

                                                                }}
                                                                size="small"
                                                                sx={{ width: '200px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="pt-2">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="tds"
                                                                variant="outlined"
                                                                value={data?.tds ?? ''}
                                                                onChange={(e) => {
                                                                    const numericValue = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                                                                    handleInvListChange(index, 'tds', numericValue)
                                                                    handleInvListChange(index, 'netAmount', calculateNetAmount((parseFloat(data?.grossAmount) || 0), (parseFloat(data?.fee) || 0), (parseFloat(data?.carry) || 0), (parseFloat(numericValue) || 0)));

                                                                }}
                                                                size="small"
                                                                sx={{ width: '150px' }}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="pt-2">
                                                        <div className="d-flex gap-1 flex-column">
                                                            <TextField
                                                                id="outlined-basic"
                                                                name="netAmount"
                                                                variant="outlined"
                                                                value={data?.netAmount ?? ''}
                                                                onChange={(e) => {
                                                                    handleInvListChange(index, 'netAmount', e.target.value)
                                                                }}
                                                                size="small"
                                                                sx={{ width: '150px' }}
                                                                disabled
                                                            />
                                                        </div>
                                                    </td>
                                                    {/* <td>
                                                        <IconButton size="" color="error" onClick={() => deleteInvestor(data?.ioRid)}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </td> */}
                                                </tr>

                                                {/* Error message row */}
                                                {data?.error && (
                                                    <tr className="">
                                                        <td colSpan="4" className="text-danger text-start fs-med-regular">
                                                            {data.error}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="4">
                                                <p className="text-center">No Records Found</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </Stack>

                    </div>
            }
        </DialogLayout>
    )
};

export default AddExit;